import { AccountField, CustomerSignUpField, EMPTY_STRING, ProfileField } from '../../../utils/forms';
import { DetailsFormField } from '../../../utils/orderForms';
import {
  companyNameSchema,
  mobileSchema,
  occupationOtherSchema,
  occupationSchema,
  orderForSchema,
  requiredSchema,
  tradeCustomerSchema,
  verificationSchema,
} from '../../SignUpForm/utils/form';

import * as yup from 'yup';

import type { SignUpFormData } from '../../../utils/forms';

export enum QuoteBuilderStep {
  DETAILS = '',
  MIX = 'mix',
  REGISTER = 'register',
  MOBILE = 'verify',
}

export enum MixFormField {
  LOCATION = 'location',
  QUANTITY = 'quantity',
  PRODUCT_TYPE_ID = 'productTypeId',
  COLOUR = 'colour',
  MPA = 'mpa',
  SLUMP = 'slump',
  AGG = 'agg',
  ADDITIVES = 'additives',
}

export enum QuoteOrSignUpValue {
  QUOTE,
  SIGNUP,
}

export interface DetailsStepContext {
  [DetailsFormField.LOCATION]:
    | {
        postcode: string;
        locality: string;
        state: string;
        lat: number;
        long: number;
      }
    | undefined;
  [DetailsFormField.PRODUCT_TYPE_ID]?: string;
  [DetailsFormField.QUANTITY]: number;
  [DetailsFormField.ADD_MESSAGE]: boolean;
}

export interface MixStepContext {
  [MixFormField.LOCATION]:
    | {
        postcode: string;
        locality: string;
        state: string;
        lat: number;
        long: number;
      }
    | undefined;
  [MixFormField.ADDITIVES]?: number;
  [MixFormField.AGG]?: number;
  [MixFormField.COLOUR]?: number;
  [MixFormField.MPA]?: number;
  [MixFormField.PRODUCT_TYPE_ID]?: string;
  [MixFormField.SLUMP]?: number;
}

export type RegisterStepContext = SignUpFormData;

export interface MobileStepContext {
  [AccountField.VERIFICATION_CODE]: string;
}

export type QuoteBuilderValues = DetailsStepContext & MixStepContext & RegisterStepContext & MobileStepContext;

// Still split for tracking purposes, see below for flattened inital values.
export const quoteStepValueKeys = {
  [QuoteBuilderStep.DETAILS]: [
    DetailsFormField.LOCATION,
    DetailsFormField.PRODUCT_TYPE_ID,
    DetailsFormField.QUANTITY,
    DetailsFormField.ADD_MESSAGE,
  ],
  [QuoteBuilderStep.MIX]: [
    MixFormField.LOCATION,
    MixFormField.ADDITIVES,
    MixFormField.AGG,
    MixFormField.COLOUR,
    MixFormField.MPA,
    MixFormField.PRODUCT_TYPE_ID,
    MixFormField.SLUMP,
  ],
  [QuoteBuilderStep.REGISTER]: [
    AccountField.IS_TRADE_CUSTOMER,
    AccountField.BUYING_BEHALF,
    AccountField.MOBILE,
    AccountField.REFERRAL,
    AccountField.REFERRAL_OTHER,
    AccountField.TERMS,
    CustomerSignUpField.BILLING_ADDRESS_LINE_1,
    CustomerSignUpField.BILLING_ADDRESS_LINE_2,
    CustomerSignUpField.COMPANY_NAME,
    CustomerSignUpField.ABN,
    CustomerSignUpField.ABN_STATE,
    CustomerSignUpField.OCCUPATION,
    CustomerSignUpField.OCCUPATION_OTHER,
    CustomerSignUpField.PREFERRED_SUPPLIER_ID,
    ProfileField.EMAIL,
    ProfileField.FIRST_NAME,
    ProfileField.LAST_NAME,
  ],
  [QuoteBuilderStep.MOBILE]: [AccountField.VERIFICATION_CODE],
};

export const initialQuoteContext: QuoteBuilderValues = {
  [DetailsFormField.LOCATION]: undefined,
  [DetailsFormField.PRODUCT_TYPE_ID]: undefined,
  [DetailsFormField.QUANTITY]: 0,
  [DetailsFormField.ADD_MESSAGE]: false,
  [MixFormField.LOCATION]: undefined,
  [MixFormField.ADDITIVES]: undefined,
  [MixFormField.AGG]: undefined,
  [MixFormField.COLOUR]: undefined,
  [MixFormField.MPA]: undefined,
  [MixFormField.PRODUCT_TYPE_ID]: undefined,
  [MixFormField.SLUMP]: undefined,
  [AccountField.IS_TRADE_CUSTOMER]: undefined,
  [AccountField.BUYING_BEHALF]: EMPTY_STRING,
  [AccountField.MOBILE]: EMPTY_STRING,
  [AccountField.REFERRAL]: EMPTY_STRING,
  [AccountField.REFERRAL_OTHER]: EMPTY_STRING,
  [AccountField.TERMS]: false,
  [AccountField.VERIFICATION_CODE]: EMPTY_STRING,
  [CustomerSignUpField.BILLING_ADDRESS_LINE_1]: EMPTY_STRING,
  [CustomerSignUpField.BILLING_ADDRESS_LINE_2]: EMPTY_STRING,
  [CustomerSignUpField.COMPANY_NAME]: EMPTY_STRING,
  [CustomerSignUpField.ABN]: EMPTY_STRING,
  [CustomerSignUpField.ABN_STATE]: EMPTY_STRING,
  [CustomerSignUpField.OCCUPATION]: EMPTY_STRING,
  [CustomerSignUpField.OCCUPATION_OTHER]: EMPTY_STRING,
  [CustomerSignUpField.PREFERRED_SUPPLIER_ID]: 0,
  [ProfileField.EMAIL]: EMPTY_STRING,
  [ProfileField.FIRST_NAME]: EMPTY_STRING,
  [ProfileField.LAST_NAME]: EMPTY_STRING,
};

export function updateQuoteCartStep<T>(step: QuoteBuilderStep, cart: QuoteBuilderValues, values: T) {
  return { ...cart, [step]: values };
}

export const quoteSignUpSchema = yup.object().shape({
  [AccountField.MOBILE]: mobileSchema,
  [AccountField.IS_TRADE_CUSTOMER]: tradeCustomerSchema,
  [CustomerSignUpField.COMPANY_NAME]: companyNameSchema,
  [CustomerSignUpField.OCCUPATION]: occupationSchema,
  [CustomerSignUpField.OCCUPATION_OTHER]: occupationOtherSchema,
  [ProfileField.FIRST_NAME]: requiredSchema,
  [ProfileField.LAST_NAME]: requiredSchema,
  [ProfileField.EMAIL]: requiredSchema,
});

export const quoteSignUpSchema2 = yup.object().shape({
  [AccountField.MOBILE]: mobileSchema,
  [AccountField.BUYING_BEHALF]: orderForSchema,
  [CustomerSignUpField.COMPANY_NAME]: companyNameSchema,
  [CustomerSignUpField.OCCUPATION]: occupationSchema,
  [CustomerSignUpField.OCCUPATION_OTHER]: occupationOtherSchema,
  [ProfileField.FIRST_NAME]: requiredSchema,
  [ProfileField.LAST_NAME]: requiredSchema,
  [ProfileField.EMAIL]: requiredSchema,
});

export const quoteVerificationSchema = yup.object().shape({
  [AccountField.VERIFICATION_CODE]: verificationSchema,
});
